import { CATEGORY_IMAGES, MAIN_CATEGORY_TABS } from '../data/productCatalog';
import { resolveMediaUrl } from './mediaUrl';

const CATEGORY_TO_MAIN_TAB = MAIN_CATEGORY_TABS.reduce((acc, tab) => {
  tab.members.forEach((member) => {
    acc[member] = tab.label;
  });
  return acc;
}, {});

const ACCENTS = ['turmeric', 'saffron', 'chili', 'cardamom', 'soil'];

const CATEGORY_ICON_RULES = [
  { match: /rice/i, icon: 'wheat' },
  { match: /spice|masala|chilli|chili|pepper|turmeric|cumin/i, icon: 'flame' },
  { match: /oil/i, icon: 'droplets' },
  { match: /flour|maida|atta/i, icon: 'circle' },
  { match: /pulse|dal|grain|lentil|semolina/i, icon: 'bean' },
  { match: /millet|ragi|sorghum/i, icon: 'sprout' },
  { match: /nut|almond|cashew|peanut/i, icon: 'nut' },
  { match: /powder|ground/i, icon: 'sparkles' },
  { match: /sugar|jaggery|sweet|candy/i, icon: 'candy' },
  { match: /flatten|aval|beaten/i, icon: 'layers' },
  { match: /vermicelli|semiya|noodle/i, icon: 'utensils' },
  { match: /pickle/i, icon: 'jar' },
  { match: /pappad|fryum|vadagam/i, icon: 'disc' },
  { match: /snack|murukku|chip/i, icon: 'cookie' },
  { match: /pooja|incense|camphor/i, icon: 'flower' },
  { match: /traditional|heritage/i, icon: 'leaf' },
];

function normalizeCategoryKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const NORMALIZED_CATEGORY_IMAGES = Object.entries(CATEGORY_IMAGES).reduce((acc, [key, image]) => {
  const normalized = normalizeCategoryKey(key);
  if (normalized) acc.set(normalized, image);
  return acc;
}, new Map());

export function getCategoryFallbackImage(name) {
  if (!name) return '/images/product-blend.png';
  if (CATEGORY_IMAGES[name]) return CATEGORY_IMAGES[name];

  const normalizedName = normalizeCategoryKey(name);
  if (!normalizedName) return '/images/product-blend.png';

  return (
    NORMALIZED_CATEGORY_IMAGES.get(normalizedName)
    || NORMALIZED_CATEGORY_IMAGES.get(normalizedName.replace(/\bs\b$/, ''))
    || '/images/product-blend.png'
  );
}

export function resolveCategoryIcon(category) {
  const haystack = `${category?.name || ''} ${category?.slug || ''}`;
  const rule = CATEGORY_ICON_RULES.find(({ match }) => match.test(haystack));
  return rule?.icon || 'package';
}

export function pickProductImage(product) {
  const raw = (
    product?.image_url
    || product?.product_detail?.gallery_urls?.[0]
    || getCategoryFallbackImage(product?.category?.name)
  );

  return resolveMediaUrl(raw);
}

export function parseAdditionalInformation(product) {
  const raw = product?.product_detail?.additional_information;
  if (typeof raw === 'object' && raw !== null) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

export function getProductHsn(product) {
  const additionalInfo = parseAdditionalInformation(product);
  if (!additionalInfo) return null;
  return additionalInfo.hsn_code || additionalInfo.hsn || additionalInfo.HSN || null;
}

export function mapApiProductToWaveItem(product) {
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    description:
      product.product_detail?.short_description
      || product.description
      || `Premium ${product.name} for export`,
    image: pickProductImage(product),
    hsn: getProductHsn(product),
    topSelling: Boolean(product.top_selling_tag),
  };
}

export function mapApiProductToDetail(product) {
  const detail = product?.product_detail || {};
  const mainImage = pickProductImage(product);
  const galleryUrls = detail.gallery_urls?.length
    ? [mainImage, ...detail.gallery_urls.map(resolveMediaUrl).filter((url) => url && url !== mainImage)]
    : [mainImage].filter(Boolean);

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    sku: product.sku,
    hsn: getProductHsn(product),
    description: product.description || detail.short_description || '',
    shortDescription: detail.short_description || product.description || '',
    category: product.category?.name || 'Uncategorized',
    categorySlug: product.category?.slug || '',
    image: pickProductImage(product),
    gallery: galleryUrls,
    topSelling: Boolean(product.top_selling_tag),
    inStock: detail.stock_status !== 'outofstock',
    stockStatus: detail.stock_status || 'instock',
    weight: detail.weight,
    reviewsCount: product.reviews_count || 0,
    hasVariants: Boolean(product.has_variants),
    variants: product.variants || [],
    relatedProductIds: detail.related_product_ids || [],
    additionalInfo: parseAdditionalInformation(product),
  };
}

export function resolveMainCategory(categoryName) {
  if (!categoryName) return MAIN_CATEGORY_TABS[MAIN_CATEGORY_TABS.length - 1].label;
  return CATEGORY_TO_MAIN_TAB[categoryName]
    || MAIN_CATEGORY_TABS[MAIN_CATEGORY_TABS.length - 1].label;
}

export function mapApiProductToCard(product, index = 0) {
  const hsn = getProductHsn(product);
  const categoryName = product.category?.name || 'Uncategorized';

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    origin: categoryName || 'India',
    tagline: product.product_detail?.short_description
      || product.description
      || `Premium ${product.name} for export`,
    tasting: hsn || product.sku || '—',
    category: categoryName,
    filter: resolveMainCategory(categoryName),
    accent: ACCENTS[index % ACCENTS.length],
    image: pickProductImage(product),
    topSelling: Boolean(product.top_selling_tag),
  };
}

export function groupProductsByCategoryId(products = []) {
  return products.reduce((groups, product) => {
    const categoryId = product?.category?.id;
    if (!categoryId) return groups;

    if (!groups[categoryId]) {
      groups[categoryId] = [];
    }

    groups[categoryId].push(product);
    return groups;
  }, {});
}

export function mapCategoryToHubItem(category) {
  const products = category.products || [];
  const featuredProduct = products.find((product) => product.image_url) || products[0];
  const rawCategoryImage = category.image_url
    || (category.image
      ? (category.image.startsWith('/') || category.image.startsWith('http')
        ? category.image
        : `/api/media/${String(category.image).replace(/\.(jpe?g|png|gif|webp|svg)$/i, '')}`)
      : null);
  const categoryImage = resolveMediaUrl(rawCategoryImage);

  return {
    id: category.id,
    label: category.name,
    slug: category.slug,
    filter: category.name,
    desc:
      category.description
      || `Explore our ${category.name} range — carefully sourced and export-ready.`,
    image: categoryImage
      || (featuredProduct ? pickProductImage(featuredProduct) : getCategoryFallbackImage(category.name)),
    icon: resolveCategoryIcon(category),
    productCount: products.length,
  };
}

export function buildCategoryFilters() {
  return ['All', ...MAIN_CATEGORY_TABS.map((tab) => tab.label)];
}
